/**
 * ARAM Platform - API Client
 * Core Desarrollos S.A.S - Copyright 2024-2026
 *
 * Typed API namespaces for all backend modules.
 * Each page should import only the namespace it needs.
 */

import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios'

// For production/Docker: use relative path (nginx proxies to backend)
// For local dev: set VITE_API_URL=/api/v1 in .env.local
const BASE_URL = '/api/v1'

// ---------------------------------------------------------------------------
// Shared Axios instance
// ---------------------------------------------------------------------------

export const apiClient = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor — supports both authStore and dedicated WMS handheld token
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    if (!config.headers.Authorization) {
      const wmsToken = typeof localStorage !== 'undefined' ? localStorage.getItem('aram_wms_operator_token') : null
      if (wmsToken) {
        config.headers.Authorization = `Bearer ${wmsToken}`
      } else if (typeof localStorage !== 'undefined') {
        try {
          const aramAuth = localStorage.getItem('aram-auth')
          if (aramAuth) {
            const parsed = JSON.parse(aramAuth)
            const token = parsed?.state?.accessToken
            if (token) {
              config.headers.Authorization = `Bearer ${token}`
            }
          }
          if (!config.headers.Authorization) {
            const stored = localStorage.getItem('auth-storage')
            if (stored) {
              const parsed = JSON.parse(stored)
              const token = parsed?.state?.accessToken
              if (token) {
                config.headers.Authorization = `Bearer ${token}`
              }
            }
          }
        } catch {}
      }
    }
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      delete config.headers['Content-Type']
    }
    return config
  },
  (error) => Promise.reject(error),
)

// Response interceptor — auto-refresh on 401
// Guards: skip if (a) the failed request IS the refresh endpoint, or (b) already retried once.
apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

    const isRefreshEndpoint = originalRequest?.url?.includes('/auth/refresh')

    if (error.response?.status === 401 && !originalRequest?._retry && !isRefreshEndpoint) {
      originalRequest._retry = true
      try {
        const { useAuthStore } = await import('@/stores/authStore')
        if (typeof useAuthStore?.getState === 'function') {
          await useAuthStore.getState().refreshAccessToken?.()
          const newToken = useAuthStore.getState().accessToken
          if (newToken) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`
            return apiClient(originalRequest)
          }
        }
      } catch {
        // fall-through to logout below
      }
      const { useAuthStore } = await import('@/stores/authStore')
      if (typeof useAuthStore?.getState === 'function') {
        useAuthStore.getState().logout?.()
      }
      if (typeof window !== 'undefined' && import.meta.env?.MODE !== 'test') {
        if (window.location.pathname.startsWith('/mobile/wms')) {
          localStorage.removeItem('aram_wms_operator_token')
          localStorage.removeItem('aram_wms_operator_session')
          delete apiClient.defaults.headers.common['Authorization']
          window.location.href = '/mobile/wms/login'
        } else {
          window.location.href = '/auth/login'
        }
      }
      return Promise.reject(error)
    }

    // Refresh endpoint itself returned 401 → token irrecoverable, logout immediately
    if (error.response?.status === 401 && isRefreshEndpoint) {
      const { useAuthStore } = await import('@/stores/authStore')
      useAuthStore.getState().logout()
      if (typeof window !== 'undefined' && window.location.pathname.startsWith('/mobile/wms')) {
        localStorage.removeItem('aram_wms_operator_token')
        localStorage.removeItem('aram_wms_operator_session')
        delete apiClient.defaults.headers.common['Authorization']
        window.location.href = '/mobile/wms/login'
      } else if (typeof window !== 'undefined') {
        window.location.href = '/auth/login'
      }
    }

    return Promise.reject(error)
  },
)

// ---------------------------------------------------------------------------
// Generic helpers
// ---------------------------------------------------------------------------

export async function get<T>(url: string, params?: Record<string, any>): Promise<T> {
  const response = await apiClient.get<T>(url, { params })
  return response.data
}

export async function post<T>(url: string, data?: any): Promise<T> {
  const response = await apiClient.post<T>(url, data)
  return response.data
}

export async function put<T>(url: string, data?: any): Promise<T> {
  const response = await apiClient.put<T>(url, data)
  return response.data
}

export async function patch<T>(url: string, data?: any): Promise<T> {
  const response = await apiClient.patch<T>(url, data)
  return response.data
}

export async function del<T>(url: string): Promise<T> {
  const response = await apiClient.delete<T>(url)
  return response.data
}

export async function upload<T>(
  url: string,
  file: File,
  onProgress?: (progress: number) => void,
): Promise<T> {
  const formData = new FormData()
  formData.append('file', file)

  const response = await apiClient.post<T>(url, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        onProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total))
      }
    },
  })

  return response.data
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface RiskEvent {
  id: string
  lat: number
  lng: number
  type: string
  title: string
  description: string
  severity: string
  timestamp: string
}

export interface PaginatedResponse<T> {
  items: T[]
  total: number
  page: number
  per_page: number
}

// ---------------------------------------------------------------------------
// API Namespaces
// ---------------------------------------------------------------------------

/** Risk Intelligence — live global risk feed */
export const riskApi = {
  getLiveFeed: () => apiClient.get<RiskEvent[]>('/risk/live-feed'),
}

/** Verticals — tenant vertical management */
export const verticalsApi = {
  list: () => apiClient.get('/verticals'),
  update: (vertical: string) => apiClient.put('/tenant/vertical', { vertical }),
}

/** Fleet — vehicles, drivers, maintenance, devices */
export const fleetApi = {
  // Vehicles
  getVehicles: (params?: Record<string, any>) => get('/fleet/vehicles', params),
  getVehicle: (id: number) => get(`/fleet/vehicles/${id}`),
  createVehicle: (data: any) => post('/fleet/vehicles', data),
  updateVehicle: (id: number, data: any) => put(`/fleet/vehicles/${id}`, data),
  deleteVehicle: (id: number) => del(`/fleet/vehicles/${id}`),
  // Drivers
  getDrivers: (params?: Record<string, any>) => get('/fleet/drivers', params),
  getDriver: (id: number) => get(`/fleet/drivers/${id}`),
  createDriver: (data: any) => post('/fleet/drivers', data),
  updateDriver: (id: number, data: any) => put(`/fleet/drivers/${id}`, data),
  deleteDriver: (id: number) => del(`/fleet/drivers/${id}`),
  // Maintenance
  getMaintenanceRecords: (params?: Record<string, any>) => get('/fleet/maintenance', params),
  createMaintenance: (data: any) => post('/fleet/maintenance', data),
  // Devices
  getDevices: (params?: Record<string, any>) => get('/fleet/devices', params),
  createDevice: (data: any) => post('/fleet/devices', data),
  // Transponders
  getTransponders: (params?: Record<string, any>) => get('/fleet/transponders', params),
  // Fuel
  getFuelRecords: (params?: Record<string, any>) => get('/fleet/fuel', params),
  createFuelRecord: (data: any) => post('/fleet/fuel', data),
  // Documents
  getDocuments: (params?: Record<string, any>) => get('/fleet/documents', params),
  // Carrier Companies
  getCarrierCompanies: (params?: Record<string, any>) => get('/fleet/carrier-companies', params),
}

/** Orders — order management */
export const ordersApi = {
  getOrders: (params?: Record<string, any>) => get('/orders', params),
  getOrder: (id: number) => get(`/orders/${id}`),
  createOrder: (data: any) => post('/orders', data),
  updateOrder: (id: number, data: any) => put(`/orders/${id}`, data),
  deleteOrder: (id: number) => del(`/orders/${id}`),
  importOrders: (file: File) => upload('/orders/import', file),
}

/** Users — admin user management */
export const usersApi = {
  getUsers: (params?: Record<string, any>) => get('/admin/users', params),
  getUser: (id: number) => get(`/admin/users/${id}`),
  createUser: (data: any) => post('/admin/users', data),
  updateUser: (id: number, data: any) => put(`/admin/users/${id}`, data),
  deleteUser: (id: number) => del(`/admin/users/${id}`),
  resetPassword: (id: number) => post(`/admin/users/${id}/reset-password`),
  toggleStatus: (id: number) => post(`/admin/users/${id}/toggle-status`),
  getStats: () => get('/admin/users/stats'),
}

/** Roles — role management */
export const rolesApi = {
  getRoles: () => get('/admin/roles'),
  getRole: (id: number) => get(`/admin/roles/${id}`),
  createRole: (data: any) => post('/admin/roles', data),
  updateRole: (id: number, data: any) => put(`/admin/roles/${id}`, data),
  deleteRole: (id: number) => del(`/admin/roles/${id}`),
}

/** Security — dashboard metrics, MFA management */
export const securityApi = {
  getMetrics: (range: string) => get('/security/metrics', { range }),
  getEvents: (range: string, limit = 50) => get('/security/events', { range, limit }),
  getAffectedUsers: (range: string) => get('/security/affected-users', { range }),
  getBlockedIPs: () => get('/security/blocked-ips'),
  unblockIP: (ip: string) => post(`/security/blocked-ips/${ip}/unblock`),
  requireMFA: (userId: number, require: boolean) =>
    apiClient.post(`/security/users/${userId}/mfa/require?require=${require}`),
  resetMFA: (userId: number) => post(`/security/users/${userId}/mfa/reset`),
  getMFAStatus: (userId: number) => get(`/security/users/${userId}/mfa/status`),
}

/** Notifications */
export const notificationsApi = {
  getAll: () => get('/notifications'),
  getUnreadCount: () => get<{ count: number }>('/notifications/unread-count'),
  markAsRead: (id: number) => put(`/notifications/${id}/read`),
  markAllAsRead: () => put('/notifications/read-all'),
}

/** Weather / SMN */
export const weatherApi = {
  getSMNAlerts: () => get('/weather/smn/alerts'),
  getRouteWeather: (params: Record<string, any>) => get('/routes/weather', params),
}

/** Tracking & Alerts */
export const trackingApi = {
  getAlerts: (params?: Record<string, any>) => get('/tracking/alerts', params),
  getAlertDetail: (id: number) => get(`/tracking/alerts/${id}`),
  acknowledgeAlert: (id: number) => post(`/tracking/alerts/${id}/acknowledge`),
}

/** Emissions / GLEC */
export const emissionsApi = {
  calculate: (data: any) => post('/emissions/calculate', data),
  getHistory: (params?: Record<string, any>) => get('/emissions/history', params),
}

/** Freight Forwarding */
export const freightApi = {
  getShipments: (params?: Record<string, any>) => get('/freight/shipments', params),
  getShipment: (id: number) => get(`/freight/shipments/${id}`),
  createShipment: (data: any) => post('/freight/shipments', data),
  updateShipment: (id: number, data: any) => put(`/freight/shipments/${id}`, data),
}

/** Geofences */
export const geofencesApi = {
  getAll: (params?: Record<string, any>) => get('/geofences', params),
  get: (id: number) => get(`/geofences/${id}`),
  create: (data: any) => post('/geofences', data),
  update: (id: number, data: any) => put(`/geofences/${id}`, data),
  delete: (id: number) => del(`/geofences/${id}`),
  toggle: (id: number) => post(`/geofences/${id}/toggle`),
  getEvents: (id: number, params?: Record<string, any>) => get(`/geofences/${id}/events`, params),
  getRecentEvents: (params?: Record<string, any>) => get('/geofences/events/recent', params),
}

/** Organizations */
export const organizationsApi = {
  getAll: () => get('/admin/organizations'),
  get: (id: number) => get(`/admin/organizations/${id}`),
  create: (data: any) => post('/admin/organizations', data),
  update: (id: number, data: any) => put(`/admin/organizations/${id}`, data),
  delete: (id: number) => del(`/admin/organizations/${id}`),
}

// ---------------------------------------------------------------------------
// Geocoding — Nominatim / OpenStreetMap (via ARAM backend proxy)
// ---------------------------------------------------------------------------

export type AddressGeocodeResult = {
  display_name?: string;
  lat: number;
  lng: number;
  street?: string | null;
  house_number?: string | null;
  locality?: string | null;
  postal_code?: string | null;
  state?: string | null;
  between_streets?: string | null;
  delivery_address?: string;
};

export async function geocodeSearch(q: string, limit = 5): Promise<AddressGeocodeResult[]> {
  const params = new URLSearchParams({ q, limit: String(limit) });
  const response = await apiClient.get<AddressGeocodeResult[]>(`/geocode/search?${params.toString()}`);
  return response.data;
}

export async function geocodeReverse(lat: number, lng: number): Promise<AddressGeocodeResult & { delivery_address: string; between_streets: string }> {
  const params = new URLSearchParams({ lat: String(lat), lng: String(lng) });
  const response = await apiClient.get<AddressGeocodeResult & { delivery_address: string; between_streets: string }>(`/geocode/reverse?${params.toString()}`);
  return response.data;
}

/** Fiscal — ARCA & Aduana */
export const fiscalApi = {
  // Credentials
  getCredentials: () => get('/fiscal/credentials'),
  getCredential: (id: number) => get(`/fiscal/credentials/${id}`),
  createCredential: (data: any) => post('/fiscal/credentials', data),
  updateCredential: (id: number, data: any) => put(`/fiscal/credentials/${id}`, data),
  deleteCredential: (id: number) => del(`/fiscal/credentials/${id}`),
  authenticateCredential: (id: number) => post(`/fiscal/credentials/${id}/authenticate`),
  // Comprobantes
  getComprobantes: (params?: Record<string, any>) => get('/fiscal/comprobantes', params),
  getComprobante: (id: number) => get(`/fiscal/comprobantes/${id}`),
  createComprobante: (data: any) => post('/fiscal/comprobantes', data),
  autorizarComprobante: (id: number) => post(`/fiscal/comprobantes/${id}/autorizar`),
  // Libro IVA
  getLibrosIVA: (params?: Record<string, any>) => get('/fiscal/libro-iva', params),
  generarLibroIVA: (data: any) => post('/fiscal/libro-iva/generar', data),
  // Aduana
  getAduanaOperaciones: (params?: Record<string, any>) => get('/fiscal/aduana/operaciones', params),
  getAduanaOperacion: (id: number) => get(`/fiscal/aduana/operaciones/${id}`),
  createAduanaOperacion: (data: any) => post('/fiscal/aduana/operaciones', data),
  updateAduanaOperacion: (id: number, data: any) => put(`/fiscal/aduana/operaciones/${id}`, data),
  // Reference Data
  getTiposCbte: () => get('/fiscal/referencia/tipos-comprobante'),
  getAlicuotasIVA: () => get('/fiscal/referencia/alicuotas-iva'),
  // Simulador
  simularImportacion: (data: any) => post('/fiscal/simulador/importacion', data),
  simularExportacion: (data: any) => post('/fiscal/simulador/exportacion', data),
  // NCM
  buscarNCM: (q: string) => get('/fiscal/ncm/search', { q }),
  // Validation
  validarCUIT: (cuit: string) => get(`/fiscal/validar-cuit/${cuit}`),
}
